/**
 * Verify Email Page — the landing for the `${APP_URL}/verify-email?token=...`
 * link in the signup email (aapastech-backend `src/routes/auth.ts`).
 *
 * Standalone like AuthCallback: no sidebar, no auth gate. It must render for a
 * signed-out recipient AND for someone already signed in, because verification
 * does not gate access to anything — so this page never redirects away on
 * failure and never strands a session. Before this route existed the catch-all
 * swallowed the link into /dashboard and the token was silently discarded.
 */
import { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/conflict';

interface VerifyEmailResult {
  verified: boolean;
  alreadyVerified: boolean;
}

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  // StrictMode mounts effects twice in dev; the endpoint is idempotent but a
  // second POST would flip a fresh verification to "already verified".
  const firedRef = useRef(false);

  const verify = useMutation<VerifyEmailResult, unknown, string>({
    mutationFn: async (t: string) => {
      const res = await api.post('/auth/verify-email', { token: t });
      return res.data.data as VerifyEmailResult;
    },
  });

  const { mutate } = verify;
  useEffect(() => {
    if (!token || firedRef.current) return;
    firedRef.current = true;
    mutate(token);
  }, [token, mutate]);

  const failed = !token || verify.isError;
  const succeeded = verify.isSuccess;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md rounded-xl border bg-white p-8 text-center shadow-sm">
        {failed ? (
          <>
            <AlertCircle className="mx-auto h-10 w-10 text-amber-500" />
            <h1 className="mt-4 text-xl font-semibold text-gray-900">
              This verification link didn&apos;t work
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              {token
                ? apiErrorMessage(verify.error, 'The link is invalid or has expired.')
                : 'The link is missing its verification token.'}
            </p>
            <p className="mt-2 text-sm text-gray-500">
              Verification links are single-use and expire. Your account still works either way, so
              you can carry on signing in.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Link
                to="/login"
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Sign in
              </Link>
              <Link
                to="/dashboard"
                className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Go to dashboard
              </Link>
            </div>
          </>
        ) : succeeded ? (
          <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" />
            <h1 className="mt-4 text-xl font-semibold text-gray-900">
              {verify.data?.alreadyVerified ? 'Already verified' : 'Email verified'}
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              {verify.data?.alreadyVerified
                ? 'This email address was verified earlier. Nothing more to do.'
                : 'Thanks — your email address is confirmed.'}
            </p>
            <Link
              to="/dashboard"
              className="mt-6 inline-block rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Continue to dashboard
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-500" />
            <p className="mt-4 text-sm text-gray-500">Verifying your email...</p>
          </>
        )}
      </div>
    </div>
  );
}
